import { getSessionToken } from 'API_LAYER/sessionToken'
import _ from 'lodash'
import { getFriendlyName } from 'Utils/FriendlyName'
import { getCurrentOrg, getUserInfo } from 'Utils/Helpers'
import { axios } from './axios'
import {
  DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP,
  FETCH_ALL_WHEN_ADMIN_ENDPOINTS,
  PAGINATED_ENDPOINTS,
  PUBLIC_ENDPOINTS
} from './constants'
import { buildListQueryParam, cursorWalkAll } from './cursorPaginatedGetAll'
import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'

/**
 *  @typedef {{ noNamespaceFilter?: boolean, extraParams?: Record<string,string> }} GetAllApiOptions
 */

/**
 *
 * @param {import('types').Endpoints} endpoint
 * @returns
 */
export function apiClient(endpoint) {
  const user = getUserInfo()
  const { tenant, apiKey } = user
  const org = getCurrentOrg()
  // Get api prefix
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'
  if (!endpoint) throw new Error('API Endpoint is null!')
  const config = {
    baseURL: `/${apiPrefix}/${tenant}/v1/${endpoint}`,
    Namespace: DEFAULT_NAMESPACE_ONLY_ENDPOINT_MAP[endpoint] ? 'default' : org,
    tenant
  }

  /**
   * @type {import('axios').AxiosRequestConfig}
   */
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      [PROCYON_SESSION_HEADER_NAME]: ''
    },
    params: {
      'ObjectMeta.Namespace': config.Namespace
    }
  }

  /** @param {GetAllApiOptions=} options */
  const listURL = (options) => {
    const { noNamespaceFilter = false, extraParams } = options || {}
    // Admins in the default org list these endpoints across every namespace.
    const isAdmin = getUserInfo()?.Spec?.UserRoles?.includes('procyon:admin')
    const isAdminRoute = FETCH_ALL_WHEN_ADMIN_ENDPOINTS[endpoint]
    const allNamespaces = noNamespaceFilter || (isAdmin && isAdminRoute && org === 'default')
    // The backend filters list results on every non-zero field (FilterCassandraRows).
    const params = Object.entries(extraParams || {}).map(
      ([k, v]) => `${k}=${encodeURIComponent(v)}`
    )
    if (!allNamespaces) params.unshift(`ObjectMeta.Namespace=${config.Namespace}`)
    return params.length ? `${config.baseURL}?${params.join('&')}` : config.baseURL
  }

  return {
    /**
     * @param {import('axios').AxiosRequestConfig['params']} prms
     * @returns
     */
    async getByParams(prms) {
      // TODO: re-enable — commented out for now (401s while testing locally)
      if (DISABLED_ENDPOINTS.includes(endpoint)) {
        console.debug(`[apiClient] getByParams skipped for disabled endpoint: ${endpoint}`)
        return null
      }
      let url = ''
      try {
        // Merge the params
        _.merge(axiosConfig.params, prms)
        const paramURL = new URLSearchParams(axiosConfig.params).toString()
        url = `${config.baseURL}${paramURL ? '?' + paramURL : ''}`
        // If fetch is running return
        if (FETCHING_KEYS_MAP[url]) return
        FETCHING_KEYS_MAP[url] = true
        const response = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return Object.values(response.data || {})[0] || null
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      } finally {
        if (url) {
          FETCHING_KEYS_MAP[url] = false
        }
      }
    },
    /**
     * @param {string} ID
     * @returns
     */
    async getByID(ID) {
      try {
        const params = {
          'ObjectMeta.ID': ID
        }
        const response = await this.getByParams(params)
        return response?.[0]
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    /**
     *
     * @param {string} name The Object Name to get
     */
    async getByName(name) {
      try {
        const r = await axios.get(`${config.baseURL}/${org}/${name}`, {
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          }
        })
        return r.data || null
      } catch (error) {}
    },
    /**
     * Like getAllShared, but a failed list resolves to the rows read before the failure.
     * @param {GetAllApiOptions} options
     * @returns {Promise<any[]>}
     */
    async getAll(options) {
      return this.getAllShared(options).catch((error) => error?.rows || [])
    },
    /**
     * Concurrent reads of one list share a request; a failed request rejects, and a failed
     * later page's error carries the rows read so far as `rows`.
     * @param {GetAllApiOptions} options
     * @returns {Promise<any[]>}
     */
    async getAllShared(options) {
      // TODO: re-enable — commented out for now (401s while testing locally)
      // Endpoints stubbed to skip the actual GET call; every fetch path
      // (slice thunks, useReduxFetch, reduxApiClient) funnels through here.
      if (DISABLED_ENDPOINTS.includes(endpoint)) {
        console.debug(`[apiClient] getAll skipped for disabled endpoint: ${endpoint}`)
        return []
      }
      const url = listURL(options)
      if (!IN_FLIGHT[url]) {
        IN_FLIGHT[url] = this.getAllOrThrow(options)
          .catch((error) => {
            console.error('[API CLIENT ERROR]()', error)
            throw error
          })
          .finally(() => delete IN_FLIGHT[url])
      }
      return IN_FLIGHT[url]
    },
    /**
     * getAllShared without the in-flight dedupe.
     * @param {GetAllApiOptions} options
     * @returns {Promise<any[]>}
     */
    async getAllOrThrow(options) {
      const url = listURL(options)
      // Cursor-paginated endpoints render the first page early and walk the rest at a larger size.
      const pagination = PAGINATED_ENDPOINTS[endpoint]
      if (pagination) {
        return this.getAllPaginated({ url, pagination, onPage: options?.onPage })
      }
      const r = await axios.get(url, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
        },
        params: {} // already done in the url
      })
      return Object.values(r.data || {})[0] || []
    },
    /**
     * Cursor walk for endpoints in PAGINATED_ENDPOINTS. Shared logic lives in
     * cursorPaginatedGetAll.js. Pages are delivered via onPage() as they arrive
     * so the UI can render progressively; the resolved value is every row.
     * @param {{ url: string, pagination: { firstPageSize: number, walkPageSize: number, maxPages?: number }, onPage?: (pageRows: any[], totalSoFar: number) => void }} param0
     * @returns {Promise<any[]>}
     */
    async getAllPaginated({ url, pagination, onPage }) {
      const fetchPage = async (queryParamValue) => {
        const r = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: { 'ObjectMeta.QueryParam': queryParamValue }
        })
        return Object.values(r.data || {})[0] || []
      }
      let failure = null
      const record = (q) =>
        fetchPage(q).catch((error) => {
          failure = error
          throw error
        })
      const rows = await cursorWalkAll({ fetchPage: record, ...pagination, onPage })
      if (failure) throw Object.assign(failure, { rows })
      return rows
    },
    async create(object) {
      try {
        object.ObjectMeta.Name = object.ObjectMeta.Name || getFriendlyName()
        object.ObjectMeta.Namespace = object.ObjectMeta.Namespace || config.Namespace
        object.ObjectMeta.Tenant = config.tenant
        const response = await axios.post(config.baseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        throw error
      }
    },
    createOrThrow(object) {
      return this.create(object)
    },
    async update(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        const response = await axios.put(objectBaseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async updateOrThrow(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        const response = await axios.put(objectBaseURL, object, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {}
        })
        return response.data
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        throw error
      }
    },
    async delete(object) {
      try {
        const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
        await axios.delete(objectBaseURL, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {} // already done in the url
        })
        /**
         * * Backend response for delete doesn't contain info on which object got deleted.
         * * Hence, we return the `object` as is.
         *  */
        return object
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    },
    async deleteOrThrow(object) {
      const objectBaseURL = createObjectBaseURL(object, { endpoint, tenant })
      const response = await axios.delete(objectBaseURL, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
        },
        params: {}
      })
      if (response?.data?.code && response.data.code !== 0) {
        throw new Error(response.data.error || response.data.message || 'Unknown error')
      }
      return object
    },
    /**
     * Single QueryParam page fetch. NOTE: the backend ignores the offset value
     * (on-switch only) and pages via the page_state cursor — startIndex cannot
     * skip ahead. Kept for the existing paginatedFetchThunk signature.
     * @param {number} startIndex ignored by the backend
     * @param {number} endIndex used as the page limit
     * @returns {Promise<any[]|null>}
     */
    async getAllByOffset(startIndex, endIndex) {
      try {
        const url = `${config.baseURL}?ObjectMeta.Namespace=${config.Namespace}`
        const response = await axios.get(url, {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
          },
          params: {
            'ObjectMeta.QueryParam': buildListQueryParam({ limit: endIndex })
          }
        })
        return Object.values(response.data || {})[0] || null
      } catch (error) {
        console.error('[API CLIENT ERROR]()', error)
        return null
      }
    }
  }
}

/**
 * Map which stores GET url endpoints which is running, to prevent duplicate api calls
 */
const FETCHING_KEYS_MAP = {}
const IN_FLIGHT = {}

// TODO: re-enable — commented out for now (401s while testing locally)
// githubresources was removed from this list: the endpoint was renamed to
// scmresources on the backend (githubaccount.proto -> scm_account.proto,
// githubresource.proto -> scm_resource.proto, Suman 2026-07). The 401s were
// the old endpoint no longer existing.
const DISABLED_ENDPOINTS = ['workloadidentitys']

const createObjectBaseURL = (object, { endpoint, tenant }) => {
  const name = object.ObjectMeta.Name
  const org = object.ObjectMeta.Namespace
  const apiPrefix = PUBLIC_ENDPOINTS.includes(endpoint) ? 'pubapi' : 'api'

  return `/${apiPrefix}/${tenant}/v1/${endpoint}/${org}/${name}`
}
